import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import UserInitials from "components/ui/form_components/UserInitials";
import {
  ContactsCreditUsageUser,
  useContactsCreditSpendByUser,
} from "lib/hooks/api/contacts/useContactsCreditSpendByUser";

/**
 * Who spent the company's contact credits over the last 12 months. Totals only: a user × month
 * cross-tab is a custom pull, not something this page tries to render.
 *
 * Owns its own (admin-only) query rather than taking the rows as a prop, so mounting this component
 * is what decides whether that query is ever sent.
 */
function ContactCreditsSpendByUser() {
  const { users, isLoading, isError } = useContactsCreditSpendByUser();

  if (isLoading) {
    return (
      <Text description color="sysTextSecondary">
        Loading spend by user…
      </Text>
    );
  }

  if (isError) {
    return (
      <Text description color="sysTextSecondary">
        We couldn&apos;t load spend by user.
      </Text>
    );
  }

  if (users.length === 0) {
    return (
      <Text description color="sysTextSecondary">
        Nobody has spent contact credits in the last 12 months.
      </Text>
    );
  }

  return (
    <UsageTable aria-label="Contact credit usage by user">
      <thead>
        <tr>
          <th scope="col">User</th>
          <th scope="col">Credits used</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <tr key={user.userGuid ?? "unattributed"}>
            <th scope="row">
              <Flex alignItems="center" gap={8}>
                {user.firstName || user.lastName ? (
                  <UserInitials firstName={user.firstName} lastName={user.lastName} />
                ) : null}
                <Flex column>
                  <Text>{displayName(user)}</Text>
                  {user.email && (
                    <Text description color="sysTextSecondary">
                      {user.email}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </th>
            <td>{numberFormatLong(user.creditsUsed)}</td>
          </tr>
        ))}
      </tbody>
    </UsageTable>
  );
}

/**
 * A user's name, falling back to their email when the profile has none (both name columns default
 * to empty), then to a label for spend the ledger recorded without an acting user.
 */
function displayName({ firstName, lastName, email }: ContactsCreditUsageUser): string {
  return `${firstName} ${lastName}`.trim() || email || "Unattributed";
}

const UsageTable = styled.table(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  textAlign: "left",
  fontSize: 14,
  "th, td": {
    padding: "8px 0",
    borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
    fontWeight: 400,
    verticalAlign: "middle",
  },
  "thead th": {
    color: theme.colors.sysTextSecondary,
    fontWeight: 500,
  },
  td: {
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  "thead th:last-of-type": {
    textAlign: "right",
  },
}));

export default ContactCreditsSpendByUser;
